import { AlertStatus } from '@/entities/alert-status';
import {
  type Reference,
  type TimeseriesLine,
  type TimeseriesMetric,
} from '@/pcs/domain/entities/pcs-timeseries';
import { AlertStatusFromValue } from '@/pcs/domain/entities/score';

export interface PCSPowerCapabilityRow {
  'mantle_pcs_status.accure_id': string;
  'frontend_id_mapping.full_frontend_id': string;
  'mantle_pcs_status.power_capability_score': number;
  'mantle_pcs_status.power_max_dch': number;
  'mantle_pcs_status.power_max_cha': number;
  'mantle_pcs_timeseries.window_end_date': Date;
  'mantle_pcs_timeseries.power_capability_dch_max': number;
  'mantle_pcs_timeseries.power_capability_dch_avg': number;
  'mantle_pcs_timeseries.power_capability_dch_min': number;
  'mantle_pcs_timeseries.power_capability_cha_max': number;
  'mantle_pcs_timeseries.power_capability_cha_avg': number;
  'mantle_pcs_timeseries.power_capability_cha_min': number;
}

export function transformPowerCapabilityRows(
  rows: PCSPowerCapabilityRow[],
): TimeseriesMetric {
  // 1) Create a map from ID => TimeseriesLine
  const linesMap: Record<string, TimeseriesLine> = {};

  // 2) Also maintain a map of top-level references (by unique name).
  //    If you want to store duplicates, you could store them in an array instead.
  const topLevelRefsMap = new Map<string, Reference>();

  for (const row of rows) {
    const id = row['mantle_pcs_status.accure_id'];
    const fullFrontendID = row['frontend_id_mapping.full_frontend_id'];
    if (!id) continue;

    // Ensure there's a line for this ID
    if (!linesMap[id]) {
      // Build line-level references
      const lineRefs: Reference[] = [
        {
          name: 'dch_power_max',
          label: 'Max Power (DCH)',
          value: row['mantle_pcs_status.power_max_dch'],
        },
        {
          name: 'cha_power_max',
          label: 'Max Power (CHA)',
          value: row['mantle_pcs_status.power_max_cha'],
        },
      ];

      // Add them to the line
      linesMap[id] = {
        id,
        fullFrontendID,
        references: lineRefs,
        idStatus:
          AlertStatusFromValue[
            row['mantle_pcs_status.power_capability_score']
          ] ?? AlertStatus.OK,
        subLines: [
          { label: 'dch_avg', data: [] },
          { label: 'dch_min', data: [] },
          { label: 'dch_max', data: [] },
          { label: 'cha_avg', data: [] },
          { label: 'cha_min', data: [] },
          { label: 'cha_max', data: [] },
        ],
      };

      // Also bubble them up into the top-level references map
      for (const ref of lineRefs) {
        // If you want to allow duplicates, skip the check
        if (!topLevelRefsMap.has(ref.name)) {
          topLevelRefsMap.set(ref.name, ref);
        }
      }
    }

    // Push timeseries data
    const timestamp = new Date(row['mantle_pcs_timeseries.window_end_date']);
    linesMap[id].subLines
      .find((s) => s.label === 'dch_avg')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_dch_avg'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'dch_min')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_dch_min'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'dch_max')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_dch_max'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'cha_avg')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_cha_avg'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'cha_min')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_cha_min'],
      });
    linesMap[id].subLines
      .find((s) => s.label === 'cha_max')
      ?.data.push({
        timestamp,
        value: row['mantle_pcs_timeseries.power_capability_cha_max'],
      });
  }

  // 3) Derive an overall metricStatus if desired (still a single pass over linesMap).
  let metricStatus: AlertStatus = AlertStatus.OK;
  for (const line of Object.values(linesMap)) {
    if (line.idStatus === AlertStatus.CRITICAL) {
      metricStatus = AlertStatus.CRITICAL;
      break; // No need to check further
    } else if (
      line.idStatus === AlertStatus.WARNING &&
      metricStatus !== AlertStatus.WARNING
    ) {
      metricStatus = AlertStatus.WARNING;
      // keep checking in case there's CRITICAL down the line
    } else if (
      line.idStatus === AlertStatus.INFO &&
      metricStatus !== AlertStatus.INFO
    ) {
      metricStatus = AlertStatus.INFO;
      // keep checking in case there's CRITICAL down the line
    }
  }

  // 4) Return the final TimeseriesMetric
  const metric: TimeseriesMetric = {
    metricName: 'Power Capability',
    metricStatus,
    // Convert top-level Map to an array
    references: Array.from(topLevelRefsMap.values()),
    lines: Object.values(linesMap),
  };

  return metric;
}
